/**
 * Hover/tap explanation for a key level tag or a confluence zone.
 *
 * Same look and placement rules as FairPriceAnchorTooltip (opens left of the
 * cursor because the tags hug the right edge; never takes the pointer, so it
 * cannot fight the crosshair that spawned it).
 */
import React from 'react';
import './FairPriceAnchorTooltip.css';

export interface KeyLevelTooltipData {
  /** Identity of what is under the cursor (a level or a zone), for de-dupe. */
  hoverId: string;
  x: number;
  y: number;
  title: string;
  lines: string[];
  color: string;
}

interface Props extends KeyLevelTooltipData {
  containerWidth: number;
  containerHeight: number;
}

const WIDTH = 300;
const MARGIN = 10;

const KeyLevelTooltip: React.FC<Props> = React.memo(({ x, y, title, lines, color, containerWidth, containerHeight }) => {
  let left = x - WIDTH - MARGIN;
  if (left < MARGIN) left = Math.min(x + MARGIN, containerWidth - WIDTH - MARGIN);
  if (left < MARGIN) left = MARGIN;
  const estHeight = 40 + lines.reduce((h, l) => h + 16 * Math.ceil(l.length / 44), 0);
  let top = y + MARGIN;
  if (top + estHeight > containerHeight - MARGIN) top = Math.max(MARGIN, y - estHeight - MARGIN);

  return (
    <div className="fp-anchor-tooltip" data-testid="key-level-tooltip" style={{ left, top, width: WIDTH }}>
      <div className="fp-anchor-tooltip-title" style={{ color }}>{title}</div>
      {lines.map((line, i) => (
        <div key={i} className={i === 0 ? 'fp-anchor-tooltip-what' : 'fp-anchor-tooltip-why'}>
          {line}
        </div>
      ))}
    </div>
  );
});

KeyLevelTooltip.displayName = 'KeyLevelTooltip';

export default KeyLevelTooltip;
